import "./BrowserNavbar.scss";
import { AppBar, Modal, TextField } from "@mui/material";
import {
  SportsSoccer,
  EmojiEventsOutlined,
  LeaderboardOutlined,
  GroupsOutlined,
  InsightsOutlined,
  MenuBookOutlined,
} from "@mui/icons-material";
import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate } from "react-router-dom";
import { useUser, emptyUser } from "../../common/contexts/userContext";
import { updateUser } from "../../services/userService";
import { clearSession } from "../../services/authStorage";
import ThemeModeToggle from "../../common/ThemeModeToggle";
import TournamentBar from "../../common/worldcup/TournamentBar";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { palette } from "../../styles/palette";

const navItems = [
  { name: "Predictions", routePath: "/bets", icon: <EmojiEventsOutlined className="browserNavbar__navIcon" /> },
  { name: "Fixtures", routePath: "/fixtures", icon: <SportsSoccer className="browserNavbar__navIcon" /> },
  { name: "Rankings", routePath: "/ranking", icon: <LeaderboardOutlined className="browserNavbar__navIcon" /> },
  { name: "Leagues", routePath: "/leagues", icon: <GroupsOutlined className="browserNavbar__navIcon" /> },
  { name: "Rules", routePath: "/rules", icon: <MenuBookOutlined className="browserNavbar__navIcon" /> },
  { name: "Info", routePath: "/worldcup-info", icon: <InsightsOutlined className="browserNavbar__navIcon" /> },
];

export const BrowserNavbar = () => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [showUpdateModal, setShowUpdateModal] = React.useState(false);
  const { user, setUser } = useUser();
  const navigate = useNavigate();

  const closeMenu = () => setAnchorElUser(null);

  function handleLogout() {
    setShowLogoutModal(false);
    clearSession();
    setUser(emptyUser);
    navigate("/login");
  }

  const menuItems = [
    { label: "Profile", action: () => navigate("/profile") },
    { label: "My Leagues", action: () => navigate("/leagues") },
    { label: "Bet data", action: () => navigate("/data") },
    { label: "FAQ", action: () => navigate("/faq") },
    { label: "Settings", action: () => setShowUpdateModal(true) },
    { label: "Log out", action: () => setShowLogoutModal(true) },
  ];

  return (
    <AppBar position="static" className="BrowserNavbarLayout">
      <div className="browserNavbar__top">
        <div className="browserNavbar__brand">
          <img
            src="./images/logo_cropped.png"
            alt="Typer logo"
            className="browserNavbar__logo"
          />
          <div className="browserNavbar__brandText">
            <span className="browserNavbar__title">Typer</span>
            <span className="browserNavbar__subtitle">World Cup 2026</span>
          </div>
        </div>

        <TournamentBar inline />

        <div className="browserNavbar__actions">
          <ThemeModeToggle compact />
          <Tooltip title="Account">
            <IconButton onClick={(e) => setAnchorElUser(e.currentTarget)} sx={{ p: 0 }}>
              <Avatar alt="profile photo" src={user.avatarUrl} />
            </IconButton>
          </Tooltip>
          <Menu
            sx={{ mt: "45px" }}
            anchorEl={anchorElUser}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            keepMounted
            open={Boolean(anchorElUser)}
            onClose={closeMenu}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.label}
                className="browserNavbar__menuItem"
                onClick={() => {
                  closeMenu();
                  item.action();
                }}
              >
                <Typography textAlign="center">{item.label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>

      <nav className="browserNavbar__nav">
        {navItems.map((page) => {
          const active = window.location.pathname.includes(page.routePath);
          return (
            <Button
              key={page.name}
              startIcon={page.icon}
              onClick={() => navigate(page.routePath)}
              className={`browserNavbar__navBtn${
                active ? " browserNavbar__navBtn--active" : ""
              }`}
            >
              {page.name}
            </Button>
          );
        })}
      </nav>

      <ModalLogout
        open={showLogoutModal}
        onClose={setShowLogoutModal}
        handleLogout={handleLogout}
      />
      <ModalUpdate open={showUpdateModal} onClose={setShowUpdateModal} />
    </AppBar>
  );
};
export default BrowserNavbar;

const updateModalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(400px, calc(100vw - 32px))",
  bgcolor: palette.bgPrimary,
  color: palette.textPrimary,
  border: `1px solid ${palette.border}`,
  borderRadius: "20px",
  boxShadow: "var(--elevation-3)",
  p: "26px 24px 22px",
  outline: "none",
};

const logoutModalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(360px, calc(100vw - 32px))",
  bgcolor: palette.bgPrimary,
  color: palette.textPrimary,
  border: `1px solid ${palette.border}`,
  borderRadius: "20px",
  boxShadow: "var(--elevation-3)",
  p: "28px 24px 22px",
  textAlign: "center" as "center",
  outline: "none",
};

interface ModalLogoutProps {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogout: () => void;
}
export const ModalLogout: React.FC<ModalLogoutProps> = ({
  open,
  onClose,
  handleLogout,
}) => (
  <Modal
    open={open}
    onClose={() => onClose(false)}
    aria-labelledby="logout-modal-title"
    aria-describedby="logout-modal-description"
  >
    <Box sx={logoutModalStyle}>
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: "auto",
          borderRadius: "9999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(229, 77, 77, 0.14)",
        }}
      >
        <LogoutRoundedIcon sx={{ fontSize: 28, color: palette.errorReject }} />
      </Box>

      <Typography
        id="logout-modal-title"
        sx={{ fontSize: "1.25rem", fontWeight: 700, mt: "16px" }}
      >
        Log out?
      </Typography>
      <Typography
        id="logout-modal-description"
        sx={{ color: palette.textSecondary, fontSize: "0.9rem", mt: "8px" }}
      >
        You'll need to sign in again to access your predictions.
      </Typography>

      <Box sx={{ display: "flex", gap: "12px", mt: "24px" }}>
        <Button
          fullWidth
          onClick={() => onClose(false)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "9999px",
            py: "9px",
            color: palette.textPrimary,
            border: `1px solid ${palette.border}`,
            "&:hover": {
              background: palette.bgElevated,
              border: `1px solid ${palette.hoverBorder}`,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          onClick={() => handleLogout()}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "9999px",
            py: "9px",
            color: "#fff",
            background: palette.errorReject,
            "&:hover": { background: palette.errorReject, filter: "brightness(0.92)" },
          }}
        >
          Log out
        </Button>
      </Box>
    </Box>
  </Modal>
);

interface ModalUpdateProps {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ModalUpdate: React.FC<ModalUpdateProps> = ({ open, onClose }) => {
  const { user, setUser } = useUser();
  const [username, setUsername] = React.useState(user.username);

  // Reset the field to the current name each time the modal is reopened, so a
  // cancelled edit doesn't linger.
  React.useEffect(() => {
    if (open) setUsername(user.username);
  }, [open, user.username]);

  function handleName(event: React.ChangeEvent<HTMLInputElement>) {
    setUsername(event.target.value);
  }

  const trimmed = username.trim();
  const canSave = trimmed.length >= 3 && trimmed !== user.username;

  function handleUpdate() {
    if (!canSave) return;
    updateUser({ id: user.id, email: user.email, username: trimmed })
      .then(() => {
        onClose(false);
        setUser({ ...user, username: trimmed });
      })
      .catch(() => alert("something went wrong"));
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleUpdate();
      return;
    }
    const specialChars = [
      "Space",
      "Backquote",
      "Minus",
      "Equal",
      "BracketLeft",
      "BracketRight",
      "Backslash",
      "Semicolon",
      "Quote",
      "Slash",
      "Comma",
    ];
    if (specialChars.includes(event.code)) {
      event.preventDefault();
    }
  };
  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="edit-username-title"
    >
      <Box sx={updateModalStyle}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "20px" }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: "9999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(249, 192, 76, 0.14)",
            }}
          >
            <PersonRoundedIcon sx={{ fontSize: 24, color: palette.accent }} />
          </Box>
          <Box>
            <Typography
              id="edit-username-title"
              sx={{ fontSize: "1.15rem", fontWeight: 700, lineHeight: 1.2 }}
            >
              Edit username
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: palette.textSecondary }}>
              This is how you appear on leaderboards.
            </Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          autoFocus
          label="Username"
          variant="outlined"
          value={username}
          onChange={handleName}
          onKeyDown={handleKeyDown}
          helperText="Letters and numbers only · at least 3 characters"
          className="browserNavbar__modalField"
        />

        <Box sx={{ display: "flex", gap: "12px", mt: "20px" }}>
          <Button
            fullWidth
            onClick={() => onClose(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "9999px",
              py: "9px",
              color: palette.textPrimary,
              border: `1px solid ${palette.border}`,
              "&:hover": {
                background: palette.bgElevated,
                border: `1px solid ${palette.hoverBorder}`,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={!canSave}
            onClick={handleUpdate}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "9999px",
              py: "9px",
              color: palette.accentText,
              background: palette.accent,
              "&:hover": { background: palette.accent, filter: "brightness(0.95)" },
              "&.Mui-disabled": {
                background: palette.bgElevated,
                color: palette.textMuted,
              },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
